// Fleet → Posture (IV-D-8; BLUEPRINT-IV §1 row 40; bp-iv-distribution.md §2.4). ONE register, and a drawer per cab.
//
// What each cab is set to — lock task, keyguard, status bar, camera, the signage restrictions, the WebView floor, Device Owner —
// against what its cohort should be: the most specific ACTIVE profile that selects it, else the CENSUS of its model (what most cabs
// of that model show today, D-IV-D-14). The register itself is RECORD-ONLY. A cab running an agent older than the posture fields
// reports a subset: that is "partial", never a fault.
//
// WRITES — only while the server says its posture writes are on (FLEET_POSTURE_WRITES; every control below is absent while off, and
// the server refuses every writer with 409 regardless). Each one goes through the fleet domain's ConfirmModal, which says what will
// change before it changes; nothing is ever issued automatically (D-IV-D-15):
//   • REMEDIATION: tick drifted cabs judged by an ACTIVE profile (one profile per batch, ≤ 250) — or open one cab's drawer — and issue
//     posture INTENTS: one per (cab, drifted key a command can set). The resolver's 5-minute tick sends each when its cab is online; the
//     cab's next snapshot satisfies it. Record-only keys and agents too old for a command are answered per cab and record nothing.
//   • PROFILES: record the census as DRAFT profiles (one per model), approve a draft (a SECOND super-admin — the server and the table
//     both refuse the author), retire an active one; and, from a cab's drawer, record THAT cab's posture as a draft profile of its own
//     (a things selector — how a deliberate posture such as the bench's is judged by its own profile, never as drift; G4-5).
//
// 3K discipline (memory screens-scale-registries-worklist): every list is server-paged and server-faceted; the chips count what the
// server counted under the same predicates as the rows; the default view is everything, ordered drift → partial → no snapshot →
// clean, so the worklist is on top and one chip away. The grammar is the fleet domain's own (Onboarding.js's Drawer, RvCard, RvRow,
// imported — never re-implemented; the BM-precedent law).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { PageHead, Tiles, DataTable, Pill, Loading, ErrorState, Button, Empty } from '../../components/ui';
import api from '../../../services/api';
import { authClient } from '../../../auth/authClient';
import { Drawer, RvCard, RvRow, ConfirmModal } from './Onboarding';
import s from './Onboarding.module.css';
import ps from './Posture.module.css';

const PAGE = 50;
const STATE_LABEL = { drift: 'drift', partial: 'partial', no_snapshot: 'no snapshot', clean: 'clean' };
const STATE_TONE = { drift: 'warn', partial: 'pend', no_snapshot: 'pend', clean: 'grow' };
const KIND_LABEL = { lock_task: 'lock task', keyguard: 'keyguard', status_bar: 'status bar', camera: 'camera', touch: 'touch',
  home: 'home app', webview: 'WebView', restrictions: 'restrictions', device_owner: 'Device Owner' };
const KEY_LABEL = (k) => (k.startsWith('restrictions.') ? k.slice(13).replace(/_/g, ' ') : k.replace(/_/g, ' '));
const show = (v) => (v === undefined || v === null ? '—' : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v));
// One drift, in words: what the cab shows, then what it should show.
const driftText = (d) => (d.key === 'webview_major_min'
  ? `WebView ${show(d.seen)}, below the floor ${show(d.want)}`
  : `${KEY_LABEL(d.key)}: ${show(d.seen)} (wants ${show(d.want)})`);

const MAX_INTENT_CABS = 250;                                   // the route's cap per request (postureV2.js MAX_INTENT_THINGS)
const errText = (e) => e?.response?.data?.error?.message || e?.response?.data?.error?.code || e.message;
const myEmail = () => { try { return authClient.getUser()?.email || null; } catch (_) { return null; } };
/** A selector in words. */
function selectorText(sel = {}) {
  if (Array.isArray(sel.things)) return sel.things.length === 1 ? `the cab ${sel.things[0]}` : `${sel.things.length} named cabs`;
  if (sel.device_model && sel.android_version) return `every ${sel.device_model} on Android ${sel.android_version}`;
  if (sel.device_model) return `every ${sel.device_model}`;
  return 'every cab';
}
const profileState = (p) => (p.active ? 'active' : p.deactivated_at ? 'retired' : 'draft');
const PROFILE_TONE = { active: 'grow', draft: 'pend', retired: 'pend' };
/** The drifted keys of a row that a command can set (the server answers the others per cab and records nothing). */
const commandableDrift = (row, meta) => (row.drift || []).filter((d) => meta.keys?.[d.key]?.commandable);
/** A cab can be remediated from this screen when writes are on and an ACTIVE profile (not the census) judges a commandable drift. */
const remediable = (row, meta) => !!(meta.writes && row.profile_source === 'profile' && row.profile_id != null && commandableDrift(row, meta).length);
/** A cab's own posture as a profile's desired map: every registry key the cab reports, at the value it shows (the WebView as its floor). */
function desiredFromObserved(row, keys) {
  const out = {};
  for (const k of Object.keys(keys || {})) {
    const v = row.observed ? row.observed[k === 'webview_major_min' ? 'webview_major' : k] : undefined;
    if (v !== undefined && v !== null) out[k] = v;
  }
  return out;
}

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
}

/** The register: server-paged (offset), server-faceted. Every change of a filter reloads from offset 0. */
function useRegister(params) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(async (nextOffset, append) => {
    setLoading(true); setError(null);
    try {
      const p = JSON.parse(key);
      const query = { limit: PAGE, offset: nextOffset };
      for (const [k, v] of Object.entries(p)) if (v) query[k] = v;
      const d = (await api.get('/v2/posture/register', { params: query })).data.data;
      setRows((prev) => (append ? [...prev, ...d.rows] : d.rows));
      setMeta(d); setOffset(nextOffset);
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [key]);
  useEffect(() => { const t = setTimeout(() => load(0, false), JSON.parse(key).q ? 300 : 0); return () => clearTimeout(t); }, [load, key]);
  return { rows, meta, loading, error, reload: () => load(0, false), more: () => load(offset + PAGE, true),
    hasMore: meta ? rows.length < Number(meta.counts.matched) : false };
}

function CabDrawer({ row, meta, onClose, onIssue, onRecordProfile }) {
  const drifted = new Set((row.drift || []).map((d) => d.key));
  // The drifted keys first — the reason the drawer was opened — then the rest, alphabetically.
  const keys = Object.keys(row.desired || {}).sort((a, b) => (drifted.has(b) - drifted.has(a)) || a.localeCompare(b));
  const judgedBy = row.profile_source === 'profile' ? `profile “${row.profile_name}”` : `the census of ${row.device_model || 'its model'}`;
  return (
    <Drawer eyebrow="Posture" title={row.thing_name} onClose={onClose}
            sub={`${row.device_model || '—'} · Android ${row.android_version || '?'} · agent ${row.agent_code ? `vc${row.agent_code}` : 'unknown'} · judged by ${judgedBy}`}
            footer={(
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-soft)' }}>
                {meta.writes ? (
                  <>
                    {remediable(row, meta) && (
                      <button type="button" className={s.btnCreate} data-action="issue-one" onClick={() => onIssue([row])}>
                        Issue posture intents
                      </button>
                    )}
                    {row.snapshot_at && (
                      <button type="button" className={s.btnTool} data-action="record-profile" onClick={() => onRecordProfile(row)}>
                        Record this cab's posture as a profile
                      </button>
                    )}
                    {!remediable(row, meta) && (
                      <span>{row.profile_source !== 'profile'
                        ? 'Judged by the census: intents are issued against an approved profile.'
                        : 'No drifted key here can be set by a command.'}</span>
                    )}
                  </>
                ) : 'Posture writes are off on this server (FLEET_POSTURE_WRITES): the register is record-only.'}
                <Link to={`/v2/fleet/device/${encodeURIComponent(row.thing_name)}`} style={{ color: 'var(--iris-text)' }}>Open the device ledger</Link>
              </span>
            )}>
      <div className={s.paneScroll}>
        <RvCard title="State" head={<Pill tone={STATE_TONE[row.state]}>{STATE_LABEL[row.state]}</Pill>}>
          <RvRow label="Last snapshot">{fmt(row.snapshot_at)}</RvRow>
          <RvRow label="Link">{row.connection_status || 'unknown'}</RvRow>
          <RvRow label="Not reported" hint="Keys this cab's agent does not report yet (agent < IV-A1) — partial, never a fault.">{String(row.unreported)}</RvRow>
        </RvCard>
        <RvCard title="Desired against observed">
          {keys.map((k) => {
            const ok = drifted.has(k);
            const seenKey = k === 'webview_major_min' ? 'webview_major' : k;
            const seen = row.observed ? row.observed[seenKey] : undefined;
            return (
              <RvRow key={k} label={KEY_LABEL(k)} hint={meta.keys?.[k]?.note}>
                <span data-key={k} data-drift={ok ? 'yes' : 'no'}>
                  {ok ? <Pill tone="warn">drift</Pill> : seen === undefined ? <Pill tone="pend">not reported</Pill> : <Pill tone="grow">ok</Pill>}{' '}
                  wants <strong>{k === 'webview_major_min' ? `≥ ${row.desired[k]}` : show(row.desired[k])}</strong> · shows <strong>{show(seen)}</strong>
                  {ok && meta.keys?.[k] && !meta.keys[k].commandable ? ' · record-only' : ''}
                </span>
              </RvRow>
            );
          })}
        </RvCard>
      </div>
    </Drawer>
  );
}

/** The recorded profiles: drafts, the active ones, the retired. Small by nature (the server returns at most 500); the census is one draft
 *  per model. Every action is a ConfirmModal, and every action is absent while the server's posture writes are off. */
function ProfilesPanel({ writes, version, ask }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const me = myEmail();
  const load = useCallback(async () => {
    setError(null);
    try { setData((await api.get('/v2/posture/profiles')).data.data); } catch (e) { setError(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load, version]);
  const act = (p, kind) => ask(kind === 'approve'
    ? { title: `Approve “${p.name}”?`, label: 'Approve',
      body: <>It becomes the ACTIVE profile for <b>{selectorText(p.selector)}</b>: the cabs it selects are judged by its {Object.keys(p.desired || {}).length} keys from the next read, and posture intents can be issued against it. An active profile of the same name is retired in the same step. Recorded by {p.created_by}; you are the second super-admin.</>,
      run: () => api.post(`/v2/posture/profiles/${p.id}/approve`, {}), done: () => `“${p.name}” is active.` }
    : { title: `Retire “${p.name}”?`, label: 'Retire', danger: true,
      body: <>The cabs it selects go back to the next most specific active profile, or to the census of their model. Pending intents recorded against it stay as they are.</>,
      run: () => api.post(`/v2/posture/profiles/${p.id}/deactivate`, {}), done: () => `“${p.name}” is retired.` });
  const recordCensus = () => ask({ title: 'Record the census as draft profiles?', label: 'Record drafts',
    body: <>One DRAFT per model, set to what most of that model's reporting cabs show today, plus the WebView floor. A draft judges nothing until a <b>second super-admin</b> approves it.</>,
    run: () => api.post('/v2/posture/profiles/census', {}), done: (d) => `Recorded ${d.profiles.length} draft profile${d.profiles.length === 1 ? '' : 's'}.` });
  const profiles = data?.profiles || [];
  return (
    <div className={s.sheet} style={{ marginTop: 18 }} data-testid="profiles">
      <div className={s.toolbar}>
        <strong style={{ fontSize: 14 }}>Profiles</strong>
        <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{profiles.filter((p) => p.active).length} active · {profiles.filter((p) => profileState(p) === 'draft').length} draft</span>
        <span className={s.spacer} />
        {writes && <button type="button" className={s.btnTool} data-action="record-census" onClick={recordCensus}>Record the census as drafts</button>}
      </div>
      {error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={error} onRetry={load} /></div>}
      {!error && !data && <div style={{ padding: 'var(--s4)', fontSize: 13, color: 'var(--ink-soft)' }}>Loading the profiles…</div>}
      {!error && data && (profiles.length === 0
        ? <Empty title="No profile recorded">Every cab is judged by the census of its model.</Empty>
        : (
          <div style={{ padding: '0 var(--s3)' }}>
            <DataTable rows={profiles} getKey={(p) => String(p.id)}
              right={<span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>recorded profiles, the active ones first</span>}
              columns={[
                { key: 'name', label: 'Profile', sortable: false, render: (p) => <span data-profile={p.id}>{p.name}</span> },
                { key: 'selector', label: 'Applies to', sortable: false, render: (p) => selectorText(p.selector) },
                { key: 'desired', label: 'Keys', width: 64, sortable: false, render: (p) => Object.keys(p.desired || {}).length },
                { key: 'state', label: 'State', width: 96, sortable: false, render: (p) => <Pill tone={PROFILE_TONE[profileState(p)]}>{profileState(p)}</Pill> },
                { key: 'created_by', label: 'Recorded · approved by', sortable: false,
                  render: (p) => <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}><span>{p.created_by}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>{p.approved_by || '—'}</span></span> },
                { key: 'act', label: '', width: 150, sortable: false, render: (p) => {
                  if (!writes) return null;
                  const st = profileState(p);
                  if (st === 'draft' && me && p.created_by === me) return <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>awaits a second super-admin</span>;
                  if (st === 'draft') return <button type="button" className={s.btnTool} data-action="approve" data-id={p.id} onClick={() => act(p, 'approve')}>Approve</button>;
                  if (st === 'active') return <button type="button" className={s.btnTool} data-action="retire" data-id={p.id} onClick={() => act(p, 'retire')}>Retire</button>;
                  return null;
                } },
              ]} />
          </div>
        ))}
    </div>
  );
}

export default function Posture() {
  const [params] = useSearchParams();
  // A link may open the register on a search (?q=…) — the device ledger and a shared URL land on the cab they name.
  const [q, setQ] = useState(() => params.get('q') || '');
  const [model, setModel] = useState('');
  const [state, setState] = useState('');
  const [kind, setKind] = useState('');
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState(() => new Map());   // thing_name → row: the cabs ticked for remediation
  const [confirm, setConfirm] = useState(null);              // {title, body, label, danger, run, done}
  const [note, setNote] = useState(null);                    // {tone: 'ok'|'err', text}
  const [busy, setBusy] = useState(false);
  const [profilesVersion, setProfilesVersion] = useState(0);
  const noteRef = useRef(null);
  // A write's answer is shown at the top of the page; bring it into view wherever the write was confirmed (the profiles sit below the register).
  useEffect(() => { if (note && noteRef.current && noteRef.current.scrollIntoView) noteRef.current.scrollIntoView({ block: 'nearest' }); }, [note]);
  const reg = useRegister({ q: q.trim(), model, state, kind });
  const m = reg.meta;
  const c = m?.counts;
  const filterKey = JSON.stringify({ q: q.trim(), model, state, kind });
  useEffect(() => { setPicked(new Map()); }, [filterKey]);   // a selection never survives a change of view

  // ONE path for every write: the ConfirmModal says what changes; Confirm runs it; the answer (or the refusal) is shown in words.
  const ask = (c2) => { setNote(null); setConfirm(c2); };
  const runConfirmed = async () => {
    const c2 = confirm; setBusy(true);
    try {
      const r = await c2.run();
      setNote({ tone: 'ok', text: c2.done(r.data.data) });
      setConfirm(null); setPicked(new Map()); setProfilesVersion((v) => v + 1); reg.reload();
    } catch (e) {
      setNote({ tone: 'err', text: errText(e) }); setConfirm(null);
    } finally { setBusy(false); }
  };
  const issue = (rows) => {
    const prof = rows[0]; const keys = new Map();
    for (const r of rows) for (const d of commandableDrift(r, m)) keys.set(d.key, (keys.get(d.key) || 0) + 1);
    ask({
      title: `Issue posture intents for ${rows.length} cab${rows.length === 1 ? '' : 's'}?`, label: 'Issue intents',
      body: (
        <div data-testid="issue-confirm">
          Against profile <b>“{prof.profile_name}”</b>: one intent per cab and drifted key a command can set —{' '}
          {[...keys.entries()].map(([k, n]) => `${KEY_LABEL(k)} (${n})`).join(', ')}.
          The 5-minute reconcile sends each one when its cab is online, and the cab's next snapshot satisfies it. A key the cab's agent
          cannot take is answered per cab and records nothing. No intent is ever created without this confirm.
        </div>
      ),
      run: () => api.post('/v2/posture/intents', { profileId: Number(prof.profile_id), thingNames: rows.map((r) => r.thing_name), confirm: true }),
      done: (d) => {
        const refused = d.results.filter((x) => !x.recorded);
        const why = [...new Set(refused.map((x) => x.error))].join(', ');
        return `Recorded ${d.recorded} posture intent${d.recorded === 1 ? '' : 's'}${refused.length ? `; ${refused.length} refused (${why})` : ''}. The next tick sends them.`;
      },
    });
  };
  const recordProfile = (row) => {
    const desired = desiredFromObserved(row, m.keys);
    ask({
      title: `Record ${row.thing_name}'s posture as a profile?`, label: 'Record draft',
      body: (
        <div data-testid="record-confirm">
          A DRAFT profile that selects <b>this cab only</b>, set to the {Object.keys(desired).length} keys it shows today. Once a{' '}
          <b>second super-admin</b> approves it, the cab is judged by its own profile — a deliberate posture (the bench's) is then never drift.
        </div>
      ),
      run: () => api.post('/v2/posture/profiles', { name: `cab: ${row.thing_name}`, selector: { things: [row.thing_name] }, desired }),
      done: (d) => `Recorded the draft “${d.name}”. A second super-admin approves it below.`,
    });
  };
  const pickRow = (r, on) => setPicked((prev) => { const next = new Map(prev); if (on) next.set(r.thing_name, r); else next.delete(r.thing_name); return next; });

  if (!m && reg.loading) return <Loading what="the posture register" />;
  if (!m && reg.error) return <ErrorState error={reg.error} onRetry={reg.reload} />;

  const models = Object.keys(c?.by_model || {}).sort();
  const stateChips = ['drift', 'partial', 'no_snapshot', 'clean'];
  const kindChips = Object.keys(KIND_LABEL).filter((k) => (c?.by_kind?.[k] || 0) > 0 || kind === k);

  return (
    <>
      <PageHead
        title="Posture"
        sub={`What each cab is set to, against its cohort's profile — or, where none is active, the census of its model. ${m.writes
          ? 'The register is a read; profiles and posture intents are written only behind a confirm.' : 'Record-only.'}`}
        actions={<Button onClick={reg.reload}>Refresh</Button>}
      />
      {!m.writes && (
        <div className={s.noteBar} data-testid="writes-off" style={{ marginBottom: 12 }}>
          Posture writes are off (FLEET_POSTURE_WRITES). Profiles cannot be recorded and no posture intent can be issued until the knob is flipped.
        </div>
      )}
      {note && (
        <div ref={noteRef} className={s.noteBar} data-testid={note.tone === 'ok' ? 'write-ok' : 'write-err'} role="status"
             style={{ marginBottom: 12, color: note.tone === 'ok' ? 'var(--grow-text)' : 'var(--warn-text, #b42318)' }}>
          {note.text}
        </div>
      )}
      <Tiles items={[
        { label: 'Cabs', value: c.total, foot: 'active, in this view' },
        { label: 'In drift', value: c.by_state.drift, tone: c.by_state.drift ? 'warn' : undefined, foot: 'differs from what it should be' },
        { label: 'Partial', value: c.by_state.partial, foot: 'agent reports a subset' },
        { label: 'No snapshot', value: c.by_state.no_snapshot, foot: 'never reported a snapshot' },
        { label: 'Clean', value: c.by_state.clean, tone: 'grow' },
        { label: 'Judged by', value: `${c.by_source.profile} / ${c.by_source.census}`, foot: 'profile / census' },
      ]} />

      <div className={s.searchBar} style={{ marginTop: 14 }}>
        <Search size={15} aria-hidden="true" style={{ color: 'var(--ink-mute)', flex: 'none' }} />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a cab or a model…" aria-label="Search the posture register" />
      </div>
      <div className={s.sheet}>
        <div className={s.toolbar} style={{ flexWrap: 'wrap', rowGap: 8 }}>
          <select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Model" className={s.btnTool}>
            <option value="">All models</option>
            {models.map((x) => <option key={x} value={x}>{x} · {c.by_model[x]}</option>)}
          </select>
          <span className={s.spacer} />
          {stateChips.map((k) => (
            <button key={k} type="button" data-chip={`state:${k}`} className={`${s.chip} ${state === k ? s.chipOn : ''}`} onClick={() => setState(state === k ? '' : k)}>
              {STATE_LABEL[k]} · {c.by_state[k]}
            </button>
          ))}
        </div>
        {kindChips.length > 0 && (
          <div className={s.toolbar} style={{ flexWrap: 'wrap', rowGap: 8, paddingTop: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>Drift in</span>
            {kindChips.map((k) => (
              <button key={k} type="button" data-chip={`kind:${k}`} className={`${s.chip} ${kind === k ? s.chipOn : ''}`} onClick={() => setKind(kind === k ? '' : k)}>
                {KIND_LABEL[k]} · {c.by_kind[k] || 0}
              </button>
            ))}
          </div>
        )}
        {m.writes && picked.size > 0 && (() => {
          const rows = [...picked.values()];
          const profiles = new Set(rows.map((r) => String(r.profile_id)));
          const problem = profiles.size > 1 ? 'tick cabs of ONE profile per batch' : rows.length > MAX_INTENT_CABS ? `at most ${MAX_INTENT_CABS} cabs per batch` : null;
          return (
            <div className={s.toolbar} data-testid="pick-bar" style={{ flexWrap: 'wrap', rowGap: 8, background: 'var(--inset)' }}>
              <span style={{ fontSize: 13 }}><b>{rows.length}</b> selected{profiles.size === 1 ? ` · profile “${rows[0].profile_name}”` : ''}</span>
              {problem && <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{problem}</span>}
              <span className={s.spacer} />
              <button type="button" className={s.btnTool} onClick={() => setPicked(new Map())}>Clear</button>
              <button type="button" className={s.btnCreate} data-action="issue" disabled={!!problem} onClick={() => issue(rows)}>Issue posture intents</button>
            </div>
          );
        })()}
        {reg.error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={reg.error} onRetry={reg.reload} /></div>}
        {!reg.error && (reg.rows.length === 0
          ? <Empty title="Nothing matches">No cab in this view.</Empty>
          : (
            <div style={{ padding: '0 var(--s3)' }}>
            <DataTable
              rows={reg.rows}
              right={<span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>shown, of {c.matched} in this view</span>}
              getKey={(r) => r.thing_name}
              columns={[
                // While writes are on: a tick box on each cab an ACTIVE profile judges and a command can bring back; nothing else is pickable.
                ...(m.writes ? [{ key: 'pick', label: '', width: 34, sortable: false,
                  render: (r) => (remediable(r, m)
                    ? <input type="checkbox" data-pick={r.thing_name} aria-label={`Select ${r.thing_name}`}
                             checked={picked.has(r.thing_name)} onChange={(e) => pickRow(r, e.target.checked)} />
                    : null) }] : []),
                { key: 'thing_name', label: 'Cab', mono: true, sortable: false,
                  render: (r) => (
                    <button type="button" data-thing={r.thing_name} onClick={() => setOpen(r)}
                            style={{ background: 'none', border: 0, padding: 0, color: 'var(--iris-text)', cursor: 'pointer', font: 'inherit' }}>
                      {r.thing_name}
                    </button>
                  ) },
                { key: 'state', label: 'State', width: 112, sortable: false, render: (r) => <Pill tone={STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</Pill> },
                { key: 'kinds', label: 'Drift', sortable: false,
                  // Capped: a cab drifting on three keys must wrap here, never push Snapshot and Link out of the frame.
                  render: (r) => (r.drift && r.drift.length
                    ? <div style={{ maxWidth: 380, whiteSpace: 'normal' }}>{r.drift.map(driftText).join(' · ')}</div>
                    : <span style={{ color: 'var(--ink-faint)' }}>—</span>) },
                // Hardware and what the cab is judged by share a cell: one adjacency, and the table fits a 1440-px frame.
                { key: 'device_model', label: 'Hardware · judged by', sortable: false,
                  render: (r) => (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
                      <span>{`${r.device_model || '—'} / Android ${r.android_version || '?'}`}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>{r.profile_source === 'profile' ? `profile ${r.profile_name}` : 'census of its model'}</span>
                    </span>
                  ) },
                { key: 'snapshot_at', label: 'Snapshot', width: 128, sortable: false, render: (r) => fmt(r.snapshot_at) },
                { key: 'connection_status', label: 'Link', width: 84, sortable: false,
                  render: (r) => <Pill tone={r.connection_status === 'online' ? 'grow' : 'pend'}>{r.connection_status || 'unknown'}</Pill> },
              ]}
            />
            </div>
          ))}
        {reg.hasMore && (
          <button type="button" className={s.loadMore} onClick={reg.more} disabled={reg.loading}>
            {reg.loading ? 'loading…' : `Load more (${reg.rows.length} of ${c.matched})`}
          </button>
        )}
        <div className={s.resultsFoot}>Results from {c.matched} of {c.total} cabs</div>
      </div>
      <ProfilesPanel writes={!!m.writes} version={profilesVersion} ask={ask} />
      {open && (
        <div className={ps.phoneFit}>
          <CabDrawer row={open} meta={m} onClose={() => setOpen(null)}
                     onIssue={(rows) => { setOpen(null); issue(rows); }} onRecordProfile={(r) => { setOpen(null); recordProfile(r); }} />
        </div>
      )}
      {confirm && (
        <ConfirmModal title={confirm.title} body={confirm.body} confirmLabel={busy ? 'Working…' : confirm.label} danger={confirm.danger}
                      onCancel={() => { if (!busy) setConfirm(null); }} onConfirm={() => { if (!busy) runConfirmed(); }} />
      )}
    </>
  );
}
